import {
	FormDataTreatmentEnum,
	IFormBaseData,
	IFormCreateData,
	IFormData,
	IFormUpdateData
}                                                  from '@app/core/interfaces/form.interface';
import { OrganizationEntityTooltipIdentifierEnum } from '@app/modules/organization/components/tooltip/organization-entity-tooltip/organization-entity-tooltip.model';
import { OrganizationFormModeEnum }                from '@app/modules/organization/forms/organization-form/organization-form.model';
import {
	Organization,
	OrganizationProp,
	OrganizationStatusEnum
}                                                  from '@app/modules/organization/models/organization.model';
import { OrganizationHelperCheck }                 from '@app/modules/organization/services/helpers';
import { Address }                                 from '@app/shared/components/address/models/address.model';
import { APP_CONFIG }                              from '@appConfig/_app-config_master';
import { DeviceTypeEnum }                          from '@appConfig/media-config';
import { ITabContent }                             from '@core/interfaces/common.interfaces';
import { environment }                             from '@env/environment';
import { EnvironmentEnum }                         from '@env/environment.model';
import {
	FeatureScopesEnum,
	OrganizationScopesEnum,
	ScopePermissionCheckEnum
}                                                  from '@models/scope.enums';
import { ActionEnum }                              from '@shared/components/action-item/action-item.enum';
import { IButtonGroupConfig }                      from '@shared/components/button-group/button-group.model';
import {
	IStatusFlow,
	OutOfFlowDisplayModeEnum
}                                                  from '@shared/components/status-flow/status-flow.model';
import { SortOrderEnum }                           from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA }                from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes
}                                                  from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                                  from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }                          from '@shared/list-operations/model/paging.model';
import { NaturalPersonProp }                       from '@shared/models/natural-person.model';
import { actionStatus }                            from '@shared/services/toaster.service';
import { ObjectUtils }                             from '@utils/helpers';


// INTERFACES ##################################################################
export interface IOrganizationMetadata {
	details: {
		dateFormat: Intl.DateTimeFormatOptions;
		dateTimeFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
	};
	entityId: string;
	entityTooltip: {
		allowedOrganizationIdentifiers: OrganizationEntityTooltipIdentifierEnum[];
		dateTimeFormat: Intl.DateTimeFormatOptions;
	};
	form: {
		name: string;
		baseData: IFormBaseData;
		buttonGroupConfig: IButtonGroupConfig;
		createData: IFormCreateData[];
		updateData: IFormUpdateData[];
		preserveData: IFormUpdateData[];
	};
	i18n: {
		basepath: string;
		[ key: string ]: string;
	};
	listOperations: IListOperationsConfig;
	status: IStatusFlow<OrganizationStatusEnum, OrganizationScopesEnum>;
	storeId: string;
}

// TYPES #######################################################################
type NaturalPersonExclusivePropsType = ( keyof Pick<Organization, 'identificationNumber'> ) | NaturalPersonProp;
type OtherLegalFormsExclusivePropsType = Exclude<OrganizationProp, 'naturalPerson' | 'identificationNumber'>;

// CONSTANTS ###################################################################
const hideConditionally = ( formItem: IFormData, localVersion: Organization, dbVersion: Organization ): boolean => {

	const areOrganizationFormModesIdentical: boolean =
		OrganizationHelperCheck.isNaturalPerson( localVersion ) && OrganizationHelperCheck.isNaturalPerson( dbVersion ) ||
		!OrganizationHelperCheck.isNaturalPerson( localVersion ) && !OrganizationHelperCheck.isNaturalPerson( dbVersion );

	// show all properties if org form modes are different
	if ( !areOrganizationFormModesIdentical ) { return false; }

	const isExclusiveToOppositeOrgFormMode: boolean =
		OrganizationHelperCheck.isNaturalPerson( localVersion )
		? (
			otherLegalFormsExclusiveProps as unknown as string[]
		).includes( formItem.formControlName )
		: (
			naturalPersonExclusiveProps as unknown as string[]
		).includes( formItem.formControlName );

	return isExclusiveToOppositeOrgFormMode;
};

// predicates for ELSTER indicator state check in forms
const isNaturalPersonMode =
	( key: string, formMode: OrganizationFormModeEnum ): boolean => key === 'organizationFormMode' && formMode === OrganizationFormModeEnum.NATURAL_PERSON
const isOtherLegalFormMode =
	( key: string, formMode: OrganizationFormModeEnum ): boolean => key === 'organizationFormMode' && formMode === OrganizationFormModeEnum.OTHER_LEGAL_FORMS;

const naturalPersonExclusiveProps: NaturalPersonExclusivePropsType[] = [
	'birthDate',
	'firstName',
	'identificationNumber',
	'lastName',
	'nameAffix',
	'namePrefix',
	'salutation',
	'title'
];

const otherLegalFormsExclusiveProps: OtherLegalFormsExclusivePropsType[] = [
	'name',
	'legalForm'
];

export const ORGANIZATION_METADATA: IOrganizationMetadata = ObjectUtils.deepCopyByRecursion( {
	details: {
		dateFormat: { day: '2-digit', month: '2-digit', year: 'numeric' },
		dateTimeFormat: { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
		internalTabContent: [
			{
				key: 'tenantId',
				order: 1,
				i18nPath: 'organization.details.tenantId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'id',
				order: 2,
				i18nPath: 'organization.details.organizationId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'parentOrgId',
				order: 3,
				i18nPath: 'shared.forms.parentOrgId.label',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'hasChildren',
				order: 4,
				i18nPath: 'organization.details.hasChildren',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			}
		]
	},
	entityId: APP_CONFIG.organizations.entityId,
	entityTooltip: {
		allowedOrganizationIdentifiers: [
			OrganizationEntityTooltipIdentifierEnum.NAME,
			OrganizationEntityTooltipIdentifierEnum.ADDRESS,
			OrganizationEntityTooltipIdentifierEnum.STATUS,
			OrganizationEntityTooltipIdentifierEnum.TAXABLE_ENTITY,
			OrganizationEntityTooltipIdentifierEnum.TAX_NUMBER,
			OrganizationEntityTooltipIdentifierEnum.VAT_ID,
			OrganizationEntityTooltipIdentifierEnum.IDENTIFICATION_NUMBER,
			OrganizationEntityTooltipIdentifierEnum.BUSINESS_IDENTIFICATION_NUMBER,
			OrganizationEntityTooltipIdentifierEnum.CREATED_DATE,
			OrganizationEntityTooltipIdentifierEnum.LAST_MODIFIED_DATE,
			OrganizationEntityTooltipIdentifierEnum.DESCRIPTION
		],
		dateTimeFormat: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
	},
	form: {
		name: 'organization',
		baseData: {
			additionalData: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.additionalData',
				order: 22,
				required: false,
				specialTreatments: null
			},
			birthDate: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isNaturalPersonMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'shared.forms.birthDate',
				order: 8,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.DATE_TO_LOCALE_STRING,
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			city: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.city',
				order: 23,
				required: true,
				specialTreatments: null
			},
			countryCode: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.countryCode',
				order: 26,
				required: false,
				specialTreatments: [ FormDataTreatmentEnum.COUNTRY_CODE_TO_I18N ]
			},
			detailedDescription: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.detailedDescription',
				order: 11,
				required: false,
				specialTreatments: null
			},
			businessIdentificationNumber: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'organization.form.businessIdentificationNumber',
				order: 17,
				required: false,
				specialTreatments: null
			},
			firstName: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isNaturalPersonMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'organization.form.firstname',
				order: 5,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			hasChildren: {
				hide: true,
				i18nPath: '',
				order: null,
				specialTreatments: null
			},
			houseNumber: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.houseNumber',
				order: 21,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: true,
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			},
			id: {
				hide: true,
				i18nPath: '',
				order: null,
				required: false,
				specialTreatments: null
			},
			identificationNumber: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isNaturalPersonMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'shared.forms.identificationNumber',
				order: 14,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			lastName: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isNaturalPersonMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'organization.form.lastname',
				order: 4,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			legalForm: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isOtherLegalFormMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'organization.form.legalForm',
				order: 13,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: true,
				specialTreatments: [
					FormDataTreatmentEnum.LEGAL_FORM_TO_I18N,
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			name: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isOtherLegalFormMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'organization.form.name',
				order: 1,
				required: true,
				specialTreatments: [ FormDataTreatmentEnum.HIDE_CONDITIONALLY ]
			},
			nameAffix: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'shared.forms.nameAffix',
				order: 7,
				required: false,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			namePrefix: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'shared.forms.namePrefix',
				order: 6,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			organizationIdentifier: {
				addSpaceToPrevious: true,
				hide: false,
				i18nPath: 'organization.form.organizationIdentifier',
				order: 9,
				required: true,
				specialTreatments: null
			},
			parentOrgId: {
				hide: true,
				i18nPath: 'shared.forms.parentOrgId',
				order: null,
				required: true,
				specialTreatments: [
					FormDataTreatmentEnum.EMPTY_PARENT_ORG_ID_TO_ROOT_LEVEL,
					FormDataTreatmentEnum.ORG_ID_TO_NAME
				]
			},
			postalCode: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.postalCode',
				order: 24,
				required: true,
				specialTreatments: null
			},
			salutation: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredCondition: isNaturalPersonMode,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'shared.forms.salutation',
				order: 2,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY,
					FormDataTreatmentEnum.SALUTATION_TO_I18N
				]
			},
			state: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.address.state',
				order: 25,
				required: false,
				specialTreatments: null
			},
			status: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.status',
				order: 18,
				required: true,
				specialTreatments: [ FormDataTreatmentEnum.STATUS_TO_I18N ]
			},
			street: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'shared.forms.address.street',
				order: 20,
				required: true,
				specialTreatments: null
			},
			taxableEntity: {
				addSpaceToPrevious: true,
				hide: false,
				i18nPath: 'organization.form.taxableEntity',
				order: 12,
				required: true,
				specialTreatments: [ FormDataTreatmentEnum.BOOLEAN_TO_I18N ]
			},
			taxNumber: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				// TODO remove condition and i18n entry when hard validation is enabled again
				i18nPath: [ EnvironmentEnum.STAGING, EnvironmentEnum.STABLE ].includes( environment.activeEnvironment )
									? 'organization.form.taxNumberHardValidationDisabled'
									: 'organization.form.taxNumber',
				order: 15,
				required: true,
				specialTreatments: null
			},
			tenantId: {
				hide: true,
				i18nPath: '',
				order: null,
				required: false,
				specialTreatments: null
			},
			title: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				hideConditionally,
				i18nPath: 'shared.forms.title',
				order: 3,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: false,
				specialTreatments: [
					FormDataTreatmentEnum.HAS_PERMISSION,
					FormDataTreatmentEnum.HIDE_CONDITIONALLY
				]
			},
			vatId: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				// TODO remove condition and i18n entry when hard validation is enabled again
				i18nPath: [ EnvironmentEnum.STAGING, EnvironmentEnum.STABLE ].includes( environment.activeEnvironment )
									? 'organization.form.vatIdHardValidationDisabled'
									: 'organization.form.vatId',
				order: 16,
				required: false,
				specialTreatments: null
			},
			website: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'organization.form.website',
				order: 10,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				required: true,
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			}
		},
		buttonGroupConfig: {
			items: [
				{ enforceLabelCustomTemplate: true, id: OrganizationFormModeEnum.NATURAL_PERSON, order: 1 },
				{ enforceLabelCustomTemplate: true, id: OrganizationFormModeEnum.OTHER_LEGAL_FORMS, order: 2 }
			]
		},
		createData: [
			{ key: 'addressData', val: new Address() },
			{ key: 'hasChildren', val: false },
			{ key: 'parentOrgId', val: 'rootLevel' },
			{ key: 'status', val: OrganizationStatusEnum.ACTIVE },
			{ key: 'taxableEntity', val: true }
		],
		updateData: [
			{ key: 'parentOrgId', fallbackValue: 'rootLevel' }
		],
		preserveData: [
			{ key: 'createdDate', fallbackValue: null },
			{ key: 'etag', fallbackValue: null },
			{ key: 'hasChildren', fallbackValue: null },
			{ key: 'id', fallbackValue: null },
			{ key: 'identificationNumber', fallbackValue: null },
			{ key: 'lastModifiedDate', fallbackValue: null },
			{ key: 'legalForm', fallbackValue: null },
			{ key: 'parentOrgId', fallbackValue: 'rootLevel' },
			{ key: 'status', fallbackValue: null },
			{ key: 'taxableEntity', fallbackValue: true }
		]
	},
	i18n: {
		basepath: APP_CONFIG.organizations.i18nPath,
		page: 'page',
		entity: 'entity',
		form: 'form',
		details: 'details'
	},
	listOperations: {
		id: ListOperationsIdentifierEnum.ORGANIZATION_LIST_PAGE,
		pagingData: {
			mode: PagingModeEnum.Light,
			continuesPagesMode: false,
			defaultValues: {
				pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
				pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
			}
		},
		searchData: {
			placeholderI18nPath: 'organization.search.placeholder'
		},
		sortData: [
			{ name: 'organizationIdentifier', i18nPath: 'organization.form.organizationIdentifier.label' },
			{ name: 'name', i18nPath: 'shared.forms.name.label' },
			{ name: 'addressData.street', i18nPath: 'shared.forms.address.street.label' },
			{ name: 'addressData.postalCode', i18nPath: 'shared.forms.address.postalCode.label' },
			{ name: 'addressData.city', i18nPath: 'shared.forms.address.city.label' },
			{ name: 'lastModifiedDate', i18nPath: 'organization.sort.lastModifiedDate.label', defaultActive: true, sortOrder: SortOrderEnum.DESC }
		],
		filterData: [
			{
				order: 1,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.FILTER,
				type: FilterTypes.FILTER_TEXT_TYPE,
				i18nPath: 'organization.filter.queryFilter',
				items: [
					{ i18nPath: 'organization.filter.queryFilter' }
				]
			},
			{
				order: 2,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.STATUS,
				type: FilterTypes.FILTER_LIST_TYPE,
				i18nPath: 'shared.forms.status',
				values: [ OrganizationStatusEnum.ACTIVE ],
				items: [
					{ value: OrganizationStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
					// { value: OrganizationStatusEnum.DELETED, i18nPath: 'shared.status.deleted'},
					{ value: OrganizationStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' }
				]
			},
			{
				order: 3,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.TAXABLE_ENTITY,
				type: FilterTypes.FILTER_OPTION_TYPE,
				i18nPath: 'organization.filter.taxableEntity',
				items: [
					{ value: true, i18nPath: 'shared.booleanTo.state.true' },
					{ value: false, i18nPath: 'shared.booleanTo.state.false' }
				]
			},
			{
				order: 4,
				isRendered: true,
				isDisabled: false,
				filterId: FilterIdentifiersEnum.HAS_VALIDATION_RESULTS,
				type: FilterTypes.FILTER_SWITCH_TYPE,
				i18nPath: 'shared.filters.hasValidationResults'
			},
		]
	},
	status: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					labelI18n: 'shared.buttons.activate',
					tooltipI18n: 'shared.buttons.activate',
					scopes: OrganizationScopesEnum.StatusEnable
				},
				dialog: {
					i18nPathButtonConfirm: 'activate',
					i18nPathDescription: 'organization.entity.dialogForActive.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.ACTIVATE,
				targetStatus: OrganizationStatusEnum.ACTIVE
			},
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: OrganizationScopesEnum.StatusDisable
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'organization.entity.dialogForDisabled.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 2,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: OrganizationStatusEnum.DISABLED
			}
		],
		flow: {
			[ OrganizationStatusEnum.ACTIVE ]: [ OrganizationStatusEnum.DISABLED, OrganizationStatusEnum.DELETED ],
			[ OrganizationStatusEnum.DELETED ]: [],
			[ OrganizationStatusEnum.DISABLED ]: [ OrganizationStatusEnum.ACTIVE ]
		}
	},
	storeId: APP_CONFIG.organizations.store.id
} );
